<template>
  <div class="contents">
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">
                <router-link to="/shop"><i class="fas fa-arrow-left"></i></router-link>
                &nbsp; Dashboard {{ shopName }}
              </h4>
            </div>
            <div class="action-btn">
              <router-link :to="'/shop/' + $route.params.id + '/reports'" class="btn btn-light mr-2">
                <i class="fas fa-chart-bar"></i> Laporan
              </router-link>
              <a :href="'/toko/' + shopSlug" target="_blank" class="btn btn-light mr-2">
                <i class="fas fa-external-link-alt"></i> Lihat Toko
              </a>
              <button class="btn btn-light" @click="loadData" :disabled="isLoading">
                <i class="fas fa-sync" :class="{ 'fa-spin': isLoading }"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading && !dashboard" class="text-center p-5">
        <div class="spinner-border text-primary"></div>
      </div>

      <template v-else-if="dashboard">
        <!-- Stats Cards -->
        <div class="row">
          <div class="col-md-3 col-6">
            <div class="card p-3 mb-3 border bg-white radius-xl stat-card">
              <div class="d-flex align-items-center">
                <div class="stat-icon bg-primary-light"><i class="fas fa-box text-primary"></i></div>
                <div class="ml-3">
                  <small class="text-muted d-block">Total Produk</small>
                  <h3 class="mb-0">{{ dashboard.stats.total_products }}</h3>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="card p-3 mb-3 border bg-white radius-xl stat-card">
              <div class="d-flex align-items-center">
                <div class="stat-icon bg-warning-light"><i class="fas fa-clock text-warning"></i></div>
                <div class="ml-3">
                  <small class="text-muted d-block">Belum Bayar</small>
                  <h3 class="mb-0 text-warning">{{ dashboard.stats.pending_orders }}</h3>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="card p-3 mb-3 border bg-white radius-xl stat-card">
              <div class="d-flex align-items-center">
                <div class="stat-icon bg-info-light"><i class="fas fa-cog text-info"></i></div>
                <div class="ml-3">
                  <small class="text-muted d-block">Sedang Diproses</small>
                  <h3 class="mb-0 text-info">{{ dashboard.stats.processed_orders }}</h3>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-6">
            <div class="card p-3 mb-3 border bg-white radius-xl stat-card">
              <div class="d-flex align-items-center">
                <div class="stat-icon bg-success-light"><i class="fas fa-check-circle text-success"></i></div>
                <div class="ml-3">
                  <small class="text-muted d-block">Order Selesai</small>
                  <h3 class="mb-0 text-success">{{ dashboard.stats.completed_orders }}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="card p-3 mb-3 border bg-white radius-xl">
              <small class="text-muted">Pendapatan Hari Ini</small>
              <h3 class="mb-1 text-primary">{{ formatRupiah(dashboard.stats.revenue_today) }}</h3>
              <small class="text-muted">
                Order {{ formatRupiah(dashboard.stats.order_revenue_today || 0) }}
                <span v-if="dashboard.stats.donation_today > 0"> · Donasi {{ formatRupiah(dashboard.stats.donation_today) }}</span>
                <span v-if="dashboard.stats.lms_today > 0"> · Kursus {{ formatRupiah(dashboard.stats.lms_today) }}</span>
              </small>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card p-3 mb-3 border bg-white radius-xl">
              <small class="text-muted">Pendapatan Bulan Ini</small>
              <h3 class="mb-1 text-primary">{{ formatRupiah(dashboard.stats.revenue_month) }}</h3>
              <small class="text-muted">
                Order {{ formatRupiah(dashboard.stats.order_revenue_month || 0) }}
                <span v-if="dashboard.stats.donation_month > 0"> · Donasi {{ formatRupiah(dashboard.stats.donation_month) }}</span>
                <span v-if="dashboard.stats.lms_month > 0"> · Kursus {{ formatRupiah(dashboard.stats.lms_month) }}</span>
              </small>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Recent Activity -->
          <div class="col-md-5">
            <div class="card p-4 mb-3 border bg-white radius-xl">
              <h5 class="mb-3">Aktivitas Terbaru</h5>
              <div v-if="!dashboard.recent_activity || dashboard.recent_activity.length === 0" class="text-muted text-center py-3">
                Belum ada aktivitas.
              </div>
              <div v-else class="activity-list">
                <div v-for="a in dashboard.recent_activity" :key="a.type + '-' + a.id" class="activity-item d-flex align-items-start mb-3">
                  <span class="activity-badge mr-2" :class="'type-' + a.type">
                    <i :class="activityIcon(a.type)"></i>
                  </span>
                  <div class="flex-grow-1">
                    <div class="d-flex justify-content-between">
                      <strong class="small">{{ a.customer || '-' }}</strong>
                      <span class="small text-muted">{{ formatTime(a.created_at) }}</span>
                    </div>
                    <div class="small text-muted">
                      <code>{{ shortRef(a.ref) }}</code>
                      · {{ formatRupiah(a.amount) }}
                    </div>
                    <span class="badge badge-sm" :class="paymentBadge(a.payment_status)">{{ a.payment_status }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Daily Sales -->
          <div class="col-md-7">
            <div class="card p-4 mb-3 border bg-white radius-xl">
              <h5>Pendapatan 30 Hari Terakhir</h5>
              <div v-if="!dashboard.daily_sales || dashboard.daily_sales.length === 0" class="text-muted py-3">Belum ada pendapatan.</div>
              <div v-else class="daily-sales-list" style="max-height: 320px; overflow-y: auto;">
                <div v-for="d in dashboard.daily_sales" :key="d.date" class="mb-2">
                  <div class="d-flex justify-content-between mb-1">
                    <small>{{ d.date }}</small>
                    <small>
                      <b>{{ formatRupiah(d.total) }}</b>
                      ({{ d.orders || 0 }} order<span v-if="d.donations">, {{ d.donations }} donasi</span><span v-if="d.enrollments">, {{ d.enrollments }} kursus</span>)
                    </small>
                  </div>
                  <div class="progress" style="height: 6px;">
                    <div class="progress-bar bg-primary" :style="{ width: barWidth(d.total) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Top Products -->
        <div class="row" v-if="dashboard.top_products">
          <div class="col-md-12">
            <div class="card p-4 mb-3 border bg-white radius-xl">
              <h5>Top 10 Produk Terlaris</h5>
              <div v-if="dashboard.top_products.length === 0" class="text-muted">Belum ada data penjualan.</div>
              <div v-else class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead><tr><th>#</th><th>Produk</th><th>Qty Terjual</th><th class="text-right">Revenue</th></tr></thead>
                  <tbody>
                    <tr v-for="(p, i) in dashboard.top_products" :key="p.product_id">
                      <td>{{ i + 1 }}</td>
                      <td>{{ p.product_name }}</td>
                      <td>{{ p.qty_sold }}</td>
                      <td class="text-right">{{ formatRupiah(p.revenue) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';

export default {
  data() { return { isLoading: false, shopSlug: '', shopName: '...' }; },
  computed: {
    ...mapState({ dashboard: s => s.shop.dashboard, currentShop: s => s.shop.currentShop }),
  },
  mounted() { this.loadData(); },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch('shop/shop_detail', id);
        await this.$store.dispatch('shop/shop_dashboard', id);
        if (this.currentShop) {
          this.shopSlug = this.currentShop.slug;
          this.shopName = this.currentShop.name;
        }
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Gagal load dashboard');
      } finally { this.isLoading = false; }
    },
    formatRupiah(v) { return 'Rp ' + (v || 0).toLocaleString('id-ID'); },
    formatTime(t) { return t ? moment(parseInt(t)).format('DD MMM HH:mm') : '-'; },
    shortRef(ref) { return ref && ref.length > 14 ? ref.slice(0, 10) + '...' : ref; },
    barWidth(amount) {
      const max = Math.max(...(this.dashboard.daily_sales || []).map(d => d.total), 1);
      return Math.max(2, (amount / max) * 100);
    },
    activityIcon(type) {
      return { order: 'fas fa-shopping-cart', donation: 'fas fa-heart', enrollment: 'fas fa-graduation-cap' }[type] || 'fas fa-circle';
    },
    paymentBadge(s) {
      return { 'badge-warning': s === 'pending', 'badge-success': s === 'paid', 'badge-danger': s === 'expired' || s === 'failed', 'badge-secondary': s === 'refund' };
    },
  },
};
</script>

<style scoped>
.stat-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 18px; }
.bg-primary-light { background: rgba(102,126,234,0.12); }
.bg-warning-light { background: rgba(245,158,11,0.12); }
.bg-info-light { background: rgba(59,130,246,0.12); }
.bg-success-light { background: rgba(16,185,129,0.12); }
.activity-badge { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0; }
.type-order { background: rgba(102,126,234,0.15); color: #667eea; }
.type-donation { background: rgba(236,72,153,0.15); color: #ec4899; }
.type-enrollment { background: rgba(16,185,129,0.15); color: #10b981; }
.badge-sm { font-size: 10px; padding: 2px 6px; }
</style>
